// Thin, no-op-safe telemetry hook (P6-5 / MR-54).
//
// The FORCE_V4 clamp uses this to emit a `requested_version` event so the legacy
// residual can be re-measured post-cutover from the client side (P6-17 — the
// only measurement that sees Path-A / CloudFront traffic, which never touches
// the Varnish/Loki edge logs).
//
// Faro is not wired until F-5/MR-70, so this MUST be a silent no-op until then —
// and for any embed where Faro failed to init or was skipped for a synthetic
// agent. It deliberately does NOT `import` @grafana/faro (no hard dependency);
// it reads the global `window.faro` instance that `initFaro` (MR-70) will set,
// and it NEVER throws: a telemetry failure must never surface to the embed.
//
// No optional chaining / `??` (the pinned ES2015 `src/` ban) — the whole access
// path is hand-guarded, and the empty catch mirrors app.vue's `parentOrigin`.
//
// Faro inits LATER THAN THIS MODULE IS USED. app.vue initialises it synchronously
// now (the WEBWIDGETV2-VERSION.txt fetch it used to await is gone — see
// src/config/buildVersion.js), but `requested_version` is emitted during
// processQueryParams, which runs EARLIER IN THE SAME TICK, i.e. still before
// window.faro exists. Removing the network wait did not change the ordering. A
// plain no-op-when-unset hook would drop that event on every load and blind the
// P6-17 cutover re-measure. So events emitted before Faro is ready are buffered
// (bounded) and flushed by flushTelemetry() once initFaro has set window.faro.
// A bot/no-collector embed never inits Faro, so its buffered events simply never
// flush — harmless.
import { normalizeStation } from '../utils/station';

const MAX_PENDING = 50;
let pending = [];

// The embed's station, stamped onto EVERY event (independent review of !219,
// D-19).
//
// Faro puts station in `user.attributes.station`, but only `stations[0]` — so on
// a multi-station embed (`stations.length > 1`, supported via requestAll; see
// app.vue's own note) every event is attributed to the first station regardless
// of which one it came from. Events that already carry their own `station`
// attribute win, so a caller with better information is never overwritten.
//
// Set here rather than threaded through call sites because the modules that
// need it most (utils/hls_engine) are several layers below any component that
// knows the station, and importing src/core/faro from there would recreate the
// incomplete-mock hazard that silently killed engine-failure reporting.
let telemetryStation = '';

export function setTelemetryStation(station) {
  try {
    telemetryStation = normalizeStation(station);
  } catch (e) {
    /* telemetry must never surface to the embed */
  }
}

// The ORIGIN of the page embedding the widget, stamped onto every event.
//
// "Which sites is this widget on, and is a failure specific to one of them" was
// previously unanswerable from telemetry alone. It is already knowable: the loader
// runs in the HOST page and appends `src=<encoded document.location.href>` to the
// iframe query (classes/widget.js), and utils/resize.js already parses exactly that,
// falling back to document.referrer, to pick a postMessage targetOrigin. This reuses
// that resolver rather than adding a second way to answer the same question.
//
// 🔑 ORIGIN, NOT THE FULL HREF, for two independent reasons.
//
// CARDINALITY: an origin is one value per embedding SITE; a full URL is one per
// PAGE, and per query-string variant of a page. MEASURED on postv4 prod: 145
// distinct embedding origins over 24h (38@15m → 100@6h → 145@24h, extrapolating to
// ~250@30d), against 214 distinct stations requesting widget.json in the same
// window. Also measured: the value lands as a free in-line attribute, NOT an
// indexed Loki label — `{service_name="faro"}` has 6 streams — so unbounded values
// cannot grow the index. The real ceiling is query-side: `max_query_series=500`,
// proven by making it fire. Origins have ~2x headroom under that; full URLs do not.
//
// ⚠️ AND IT IS STILL SELF-REPORTED, which does NOT contradict the above — the two
// claims are about different things, and an earlier version of this comment made
// them read as if they did. `src` is set by the embedding page's copy of the loader,
// so an embedder can put anything there: the value is not TRUSTWORTHY. What bounds
// cardinality is not trust, it is that the honest population is ~145 and there is no
// mechanism by which ordinary traffic mints new ones. A deliberate abuser can mint
// unbounded values and blow past max_query_series — that is a known, accepted,
// unmitigated exposure of an unauthenticated public collector (there is no cap,
// allowlist, truncation or normalisation anywhere in this path), not a property this
// field claims to have. Fine for analytics; do not build authorization on it.
//
// The document.referrer fallback is browser-set and harder to forge, but modern
// Referrer-Policy degrades it to origin-only cross-origin — which is exactly this
// field, so the fallback loses nothing.
//
// DISCLOSURE: the href carries the host page's query string — session tokens, email
// addresses, whatever the customer's CMS puts there. `originFromCandidates` returns
// scheme+host only, so the query never leaves the iframe by THIS path.
//
// 🪤 An earlier version of this note went on to say Faro "would be a NEW place it is
// RETAINED", i.e. that declining to stamp the href here avoided the retention.
// It did not, and independent review measured it: the SDK's `createPageMeta` is
// registered unconditionally and sets `meta.page.url` to the IFRAME's location — and
// the loader builds that location by copying the host page's ENTIRE query string
// into it and appending `src=<encoded host href>`. The full href was in Faro on
// every payload the whole time, in the one field the analysis never looked at.
// Declining to add a second copy is not a privacy control. The retention itself is
// now closed by `redactPageUrl`/`beforeSend` in core/faro.js; keep them together.
let telemetryHost = '';

// An upper bound on the stamped value. NOT a cardinality control — the note above is
// explicit that a deliberate abuser can mint unbounded distinct origins and that this
// is accepted. This bounds the SIZE of any single one, which is a different and much
// cheaper property: the value is attacker-supplied and goes into every payload.
//
// 300 is chosen so it cannot reject a real origin. A hostname is at most 253 octets
// (RFC 1035), so the longest legitimate `https://<host>:<port>` is ~265; review
// measured a 300-character label arriving as a 316-character host_origin. Anything
// past 300 is not an origin, so it is dropped rather than truncated — a truncated
// origin is a value that looks real and is not.
const MAX_HOST_ORIGIN = 300;

export function setTelemetryHost(origin) {
  try {
    const next = origin ? String(origin) : '';
    telemetryHost = next.length > MAX_HOST_ORIGIN ? '' : next;
  } catch (e) {
    // 🪤 CLEAR, DO NOT LEAVE THE PREVIOUS VALUE. `String(origin)` throwing used to
    // leave `telemetryHost` at whatever the last successful call set, so a failed
    // re-resolve kept stamping the OLD site's origin onto the new one's events —
    // silently attributing traffic to the wrong embedder, which is worse than no
    // attribution. The only test on this path asserted that the call does not throw,
    // which the previous form satisfied.
    telemetryHost = '';
  }
}

// Stamp the embed's context (station + host origin) onto an event's attributes.
// Renamed from `withStation` when the host origin joined it — a decorator that adds
// two things should not be named for one of them.
function withEmbedContext(attributes) {
  const attrs = attributes || {};
  // Always a COPY, including on the nothing-to-add path, which used to hand the
  // caller's own object straight through to `pushEvent`. Faro retains the attributes
  // it is given, so a caller that reused or mutated its object after the call could
  // change an already-pushed event's payload — a shared-mutable-state bug that only
  // appears under a specific call shape, which is the kind that survives review.
  const out = {};
  const keys = Object.keys(attrs);
  for (let i = 0; i < keys.length; i += 1) out[keys[i]] = attrs[keys[i]];
  if (!telemetryStation && !telemetryHost) return out;
  // Never clobber a caller-supplied value — a caller with better information wins,
  // independently for each field.
  //
  // 🪤 hasOwnProperty, NOT falsiness. `!out.station` also overwrites a value the
  // caller supplied DELIBERATELY EMPTY (`{ station: '' }` — "I know there is no
  // station here"), replacing the caller's better information with the module's
  // global. Presence of the key is the caller's signal; its truthiness is not ours
  // to judge.
  const has = (k) => Object.prototype.hasOwnProperty.call(out, k);
  if (telemetryStation && !has('station')) out.station = telemetryStation;
  if (telemetryHost && !has('host_origin')) out.host_origin = telemetryHost;
  return out;
}

function faroApi() {
  const w = typeof window !== 'undefined' ? window : undefined;
  const faro = w && w.faro;
  if (faro && faro.api && typeof faro.api.pushEvent === 'function') {
    return faro.api;
  }
  return undefined;
}

export function pushTelemetryEvent(name, attributes) {
  try {
    const api = faroApi();
    const attrs = withEmbedContext(attributes);
    if (api) {
      api.pushEvent(name, attrs);
    } else if (pending.length < MAX_PENDING) {
      // Stamp at PUSH time, not flush time: a buffered event belongs to the
      // station configured when it happened.
      pending.push([name, attrs]);
    }
  } catch (e) {
    /* telemetry must never surface to the embed */
  }
}

// Drain events buffered before Faro was ready. Idempotent (clears the buffer),
// a safe no-op when Faro never initialized, and never throws. Called once right
// after initFaro (F-5/MR-70).
export function flushTelemetry() {
  try {
    const api = faroApi();
    if (!api) {
      return;
    }
    const queued = pending;
    pending = [];
    for (let i = 0; i < queued.length; i += 1) {
      api.pushEvent(queued[i][0], queued[i][1]);
    }
  } catch (e) {
    /* telemetry must never surface to the embed */
  }
}
